/**
 * @fileoverview Candidacy Detail Page
 * 
 * Detail page for rental candidacies using UniversalDetailPage system.
 * Includes custom sections for checklist, residents, actions, and status management.
 * 
 * @module Screens/Candidacies/Detail
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '@clerk/clerk-react';
import { UniversalDetailPage } from 'shared/components/UniversalDetailPage';
import { CANDIDACY_ENTITY_CONFIG } from 'shared/configs/entities/candidacy.config';
import { CandidacyChecklistSection } from './components/CandidacyChecklistSection';
import { CandidacyResidentsSection } from './components/CandidacyResidentsSection';
import { CandidacyActionsSection } from './components/CandidacyActionsSection';
import { CandidacyHeaderActions } from './components/CandidacyHeaderActions';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';

const REASON_LABELS: Record<string, string> = {
  reasonTooSmall: 'Te klein',
  reasonTooLarge: 'Te groot',
  reasonTooExpensive: 'Te duur',
  reasonBadLocation: 'Slechte locatie',
  reasonBadInside: 'Slechte afwerking',
  reasonOther: 'Andere reden',
  reasonPreferNotToSay: 'Wil niet zeggen',
};

export const CandidacyDetail = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useSession();
  const [allCandidacies, setAllCandidacies] = useState<any[]>([]);
  
  // Get candidacy data from navigation state (if available) or find in allCandidacies
  const candidacyFromState = useMemo(() => {
    // First, try to get from navigation state
    if (location.state?.candidacy) {
      return location.state.candidacy;
    }
    
    // If not in state, try to find in allCandidacies (for when ID changes)
    if (allCandidacies.length > 0 && id) {
      return allCandidacies.find(c => c.id.toString() === id);
    }
    
    return undefined;
  }, [location.state?.candidacy, allCandidacies, id]);

  // Fetch all candidacies for navigation
  const fetchAllCandidacies = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/get-appointments`,
        { method: "GET" },
        session
      );

      if (response.ok) {
        const data = await response.json();
        setAllCandidacies(data);
      }
    } catch (error) {
      console.error("Error fetching all candidacies:", error);
    } 
  }, [session]);

  useEffect(() => {
    fetchAllCandidacies();
  }, [fetchAllCandidacies]);

  // Custom navigation handler that passes candidacy data through state
  const handleCustomNavigation = useCallback((targetId: string | number, searchParams: URLSearchParams) => {
    // Find the candidacy data in allCandidacies
    const candidacy = allCandidacies.find(c => c.id.toString() === targetId.toString());
    
    // Preserve search params
    const params = new URLSearchParams(searchParams);
    const url = `/rental/candidacies/${targetId}?${params.toString()}`;
    
    // Navigate with candidacy data in state
    navigate(url, {
      state: { candidacy }
    });
  }, [allCandidacies, navigate]);

  // Enhanced config with custom sections and navigation
  const enhancedConfig = useMemo(() => ({
    ...CANDIDACY_ENTITY_CONFIG,
    navigation: {
      ...CANDIDACY_ENTITY_CONFIG.navigation,
      customNavigationHandler: handleCustomNavigation
    },
    plugins: {
      ...CANDIDACY_ENTITY_CONFIG.plugins,
      customSections: [
        // Header actions (Approve, Reject) - in header next to inter-page nav
        {
          id: 'header-actions',
          position: 'header-right' as const,
          component: CandidacyHeaderActions,
          componentProps: {
            onRefresh: () => window.location.reload() // Simple refresh after action
          }
        },
        // Actions section (Send Invite, Date Selection)
        {
          id: 'actions',
          position: 'before-content' as const,
          component: CandidacyActionsSection,
          componentProps: {
            onRefresh: () => window.location.reload() // Simple refresh after action
          }
        },
        // Checklist section
        {
          id: 'checklist',
          position: 'after-content' as const,
          component: CandidacyChecklistSection,
          condition: (candidacy: any) => candidacy?.checklist && candidacy.checklist.length > 0
        },
        // Residents section
        {
          id: 'residents',
          position: 'after-content' as const,
          component: CandidacyResidentsSection,
          condition: (candidacy: any) => candidacy?.residents && candidacy.residents.length > 0
        },
      ]
    }
  }), [handleCustomNavigation]);

  return (
    <UniversalDetailPage
      entityConfig={enhancedConfig}
      entityId={id}
      initialEntity={candidacyFromState}
      allEntities={allCandidacies}
      renderAfterFields={(entity) => {
        const reason = entity?.notInterested?.reason;
        const details = entity?.notInterested?.details;
        if (!reason && !details) return null;
        return (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-red-700">Geen interesse</p>
            {reason && (
              <div className="mb-1">
                <span className="text-xs text-red-500">Reden: </span>
                <span className="text-sm text-red-800">{REASON_LABELS[reason] ?? reason}</span>
              </div>
            )}
            {details && (
              <div>
                <span className="text-xs text-red-500">Toelichting: </span>
                <span className="text-sm text-red-800">{details}</span>
              </div>
            )}
          </div>
        );
      }}
    />
  );
};

export default CandidacyDetail;

